import { useQuery } from "@tanstack/react-query";
import { parse as parseBestEffortJsonStream } from "best-effort-json-parser";
import { addDays } from "date-fns";
import type React from "react";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";

import type {
  MassPostReachDto,
  MessageCreateRequest,
  MessageMultipleProjectsCreateRequest,
  PostTemplateDto,
  SimpleProjectDto,
  SystemGroupDto,
} from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { CommunityPostReachDetailsModal } from "@/components/CommunityFeed/CommunityPostReachDetailsModal";
import { CommunityPostStatus } from "@/components/CommunityFeed/types";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { CreatePostAppPreview } from "@/components/CreatePostAppPreview/CreatePostAppPreview";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FileQuestion01Icon } from "@/components/Icons/FileQuestion01Icon";
import { MessageSmileSquareIcon } from "@/components/Icons/MessageSmileSquareIcon";
import { PencilLineIcon } from "@/components/Icons/PencilLineIcon";
import { Settings04Icon } from "@/components/Icons/Settings04Icon";
import { XCloseIcon } from "@/components/Icons/XCloseIcon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { ActionBar } from "@/design-system/ActionBar";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Info } from "@/design-system/Info";
import { Inline } from "@/design-system/Inline";
import { SectionCard } from "@/design-system/SectionCard";
import { processJsonStream } from "@/helpers/processJsonStream";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useUploadDocument } from "@/hooks/Network/useUploadDocument";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useUploadVideo } from "@/hooks/Network/useUploadVideo";
import { useBool } from "@/hooks/useBool";
import { useMutationAbortController } from "@/hooks/useMutationAbortController";
import { useSlug } from "@/hooks/useSlug";
import type { AdminCreateMassPostFormValues } from "@/modules/community-feed/pages/admin-create-post/AdminCreatePostPage";
import { AdminCreatePostFormAIHelper } from "@/modules/community-feed/pages/admin-create-post/components/AdminCreatePostFormAIHelper";
import { AdminCreatePostFormEditor } from "@/modules/community-feed/pages/admin-create-post/components/AdminCreatePostFormEditor";
import { AdminCreatePostFormScheduleSelector } from "@/modules/community-feed/pages/admin-create-post/components/AdminCreatePostFormScheduleSelector";
import { AdminCreatePostFormTemplateSelector } from "@/modules/community-feed/pages/admin-create-post/components/AdminCreatePostFormTemplateSelector";
import { AdminCreatePostFormTypeSelector } from "@/modules/community-feed/pages/admin-create-post/components/AdminCreatePostFormTypeSelector";
import { AdminCreatePostTipsModal } from "@/modules/community-feed/pages/admin-create-post/components/AdminCreatePostTipsModal";
import { useConfig } from "@/providers/config/useConfig";
import { communityFeedMutations } from "@/queries/community-feed/mutations";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { generalMutations } from "@/queries/general/mutations";
import { usePortfolioQueries } from "@/queries/portfolio/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { CreateMassPostFormAudienceSelector } from "./components/CreateMassPostFormAudienceSelector";
import { CreateMassPostRedesignForm } from "./components/CreateMassPostRedesignForm";

export function CreateMassPostPage(): React.ReactNode {
  // TODO: Replace with a better history-management solution
  const [originalText, setOriginalText] = useState("");
  const [copilotText, setCopilotText] = useState("");
  const [isCopilotText, isCopilotTextHandlers] = useBool();
  const [templateInUse, setTemplateInUse] = useState<PostTemplateDto>();
  const [reach, setReach] = useState<MassPostReachDto>();
  const [isSuccessModalOpened, successModalHandler] = useBool();
  const [isSubmitting, isSubmittingHandlers] = useBool();
  const [isCancelModalOpened, cancelModalHandler] = useBool();
  const [isTipsModalOpened, tipsModalHandler] = useBool();
  const [isConfirmModalOpened, confirmModalHandler] = useBool();

  const showFlashToast = useFlashToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const slug = useSlug();
  const sessionUser = useSessionUser();
  const useNewUnifiedPostCreation = useConfig("useNewUnifiedPostCreation");
  const { uploadFormImage } = useUploadImage();
  const { uploadFormVideo, abortUploadVideos } = useUploadVideo({});
  const { uploadFormDocument } = useUploadDocument();
  const abortController = useMutationAbortController();
  const copilotResponseMutation = generalMutations.useGetCopilotResponse();
  const addMassMessageMutation = communityFeedMutations.useAddMassMessage();

  const portfolioQueries = usePortfolioQueries();
  const communityFeedQueries = useCommunityFeedQueries();

  const {
    data: templates,
    isPending: isPendingTemplates,
    isError: isErrorTemplates,
  } = useQuery(
    communityFeedQueries.listMessageTemplates({
      isPortfolio: true,
    }),
  );
  const {
    data: portfolioPermissions,
    isPending: isPendingPortfolioPermissions,
    error: errorPortfolioPermissions,
  } = useQuery(portfolioQueries.permissions());

  let allowedPostTypes: MessageMultipleProjectsCreateRequest["type"][] = [];
  if (portfolioPermissions?.canMassMessageAnnouncementPost) {
    allowedPostTypes = ["announcementReadOnly", "announcement"];
  } else if (portfolioPermissions?.canMassMessageGeneralPost) {
    allowedPostTypes = ["undefined"];
  }
  const useRedesignedForm = useNewUnifiedPostCreation && Boolean(portfolioPermissions?.canMassMessageAnnouncementPost);
  const defaultFormValues: AdminCreateMassPostFormValues = {
    title: "",
    content: "",
    postType: allowedPostTypes.length === 1 ? allowedPostTypes[0] : undefined,
    group: undefined,
    projects: [],
    images: [],
    videos: [],
    documents: [],
    poll: undefined,
    scheduleDate: undefined,
    isEmailPreviewOnly: true,
  };
  const form = useForm<AdminCreateMassPostFormValues>({
    defaultValues: defaultFormValues,
  });
  const currPostType = useWatch({
    name: "postType",
    control: form.control,
  });
  const currTitle = useWatch({
    name: "title",
    control: form.control,
  });
  const currContent = useWatch({
    name: "content",
    control: form.control,
  });
  const currImages = useWatch({
    name: "images",
    control: form.control,
  });
  const currVideos = useWatch({
    name: "videos",
    control: form.control,
  });
  const currDocuments = useWatch({
    name: "documents",
    control: form.control,
  });
  const currGroup = useWatch({
    name: "group",
    control: form.control,
  });
  const currProjects = useWatch({
    name: "projects",
    control: form.control,
  });
  const currScheduleDate = useWatch({
    name: "scheduleDate",
    control: form.control,
  });
  const currPoll = useWatch({
    name: "poll",
    control: form.control,
  });

  useEffect(() => {
    form.register("projects", {
      validate: (value) => {
        if (value.length === 0) {
          return t("page.admin-create-post.form.projects.error");
        }
      },
    });
  }, [form, t]);

  const reset = () => {
    form.reset();
    setReach(undefined);
    setCopilotText("");
    setOriginalText("");
    isCopilotTextHandlers.setFalse();
    isSubmittingHandlers.setFalse();
  };

  const createCommunityContent = async ({
    formValues,
    imageIds,
    videoIds,
    documentIds,
  }: {
    formValues: AdminCreateMassPostFormValues;
    imageIds: string[];
    videoIds: string[];
    documentIds: string[];
  }) => {
    const postType = formValues.postType ?? (useRedesignedForm ? "announcementReadOnly" : undefined);
    if (!postType) {
      return;
    }

    const data: MessageMultipleProjectsCreateRequest = {
      type: postType,
      title: formValues.title,
      content: formValues.content,
      imageIds,
      videoIds,
      documentIds,
      systemGroupId: formValues.group?.id,
      projectSelected: formValues.projects.map((project) => project.id),
      scheduledFor: formValues.scheduleDate?.toISOString(),
      aiTemplateId: templateInUse?.id,
      isEmailPreviewOnly: formValues.isEmailPreviewOnly,
    };

    if (formValues.poll) {
      // If the post is scheduled, the poll end date should be calculated based on the schedule date.
      const pollEndDate = formValues.scheduleDate
        ? addDays(formValues.scheduleDate, formValues.poll.durationInDays)
        : addDays(new Date(), formValues.poll.durationInDays);

      data.poll = {
        allowMultipleAnswers: formValues.poll.allowMultipleAnswers,
        answers: formValues.poll.options,
        endsAt: pollEndDate.toISOString(),
        question: formValues.poll.question,
      };
    }

    successModalHandler.setTrue();

    try {
      const res = await addMassMessageMutation.mutateAsync({
        data,
      });

      setReach(res.reach);
    } catch {
      successModalHandler.setFalse();
    } finally {
      isSubmittingHandlers.setFalse();
    }
  };

  const handleSubmit = async (formValues: AdminCreateMassPostFormValues) => {
    isSubmittingHandlers.setTrue();

    const imageUploadPromises = formValues.images.map((image) => uploadFormImage(image));
    // Curently only 1 document is allowed
    const documentUploadPromises = formValues.documents.map((document) => uploadFormDocument(document));
    // Curently only 1 video is allowed
    const videoUploadPromises = formValues.videos.map((video) => uploadFormVideo(video));
    const imageIds = (await Promise.allSettled(imageUploadPromises))
      .map((image) => (image.status === "fulfilled" ? image.value?.id : ""))
      .filter(Boolean) as string[];
    const documentIds = (await Promise.allSettled(documentUploadPromises))
      .map((document) => (document.status === "fulfilled" ? document.value?.id : ""))
      .filter(Boolean) as string[];
    const videoIds = (await Promise.allSettled(videoUploadPromises.map((video) => video)))
      .map((video) => (video.status === "fulfilled" ? video.value?.id : ""))
      .filter(Boolean) as string[];

    // Only create message when all attachments were uploaded successfully
    if (
      imageIds.length === formValues.images.length &&
      documentIds.length === formValues.documents.length &&
      videoIds.length === formValues.videos.length
    ) {
      await createCommunityContent({
        formValues,
        imageIds,
        videoIds,
        documentIds,
      });
    } else {
      showFlashToast({
        variant: "alert",
        message: t("page.message-feed.create.notifications.error"),
      });
      isSubmittingHandlers.setFalse();
    }
  };

  const handleCreatePost = async () => {
    confirmModalHandler.setFalse();
    await handleSubmit(form.getValues());
  };

  const handleCancel = () => {
    // Cancel all ongoing video uploads
    abortUploadVideos(form.getValues("videos"));

    void navigate(
      routes.communityFeed.list({
        slug,
      }),
    );
  };

  const handleGenerateContent = async () => {
    const content = form.getValues("content");
    const result = await copilotResponseMutation.mutateAsync({
      userInput: content,
      copilotOptions: [],
      signal: abortController.getSignal(),
    });

    if (!result.ok || !result.body) {
      throw new Error("Failed to get copilot response");
    }

    setOriginalText(content);
    isCopilotTextHandlers.setTrue();
    let buffer = "";
    await processJsonStream<{ chunk: string }>({
      stream: result.body,
      callbacks: {
        onChunk: (result) => {
          buffer += result.chunk;

          const parsedJson = parseBestEffortJsonStream(buffer) as { title?: string; content?: string };
          if (typeof parsedJson?.title === "string" && parsedJson.title) {
            form.setValue("title", parsedJson.title, {
              shouldDirty: true,
            });
          }

          if (typeof parsedJson?.content === "string" && parsedJson.content) {
            setCopilotText(parsedJson.content);
            form.setValue("content", parsedJson.content, {
              shouldDirty: true,
            });
          }
        },
        onError: () => {
          showFlashToast({
            variant: "alert",
            message: t("page.admin-create-post.form.copilot.error"),
          });
        },
      },
    });
  };

  const handleUndoContent = () => {
    form.setValue("content", originalText, {
      shouldDirty: true,
    });
    isCopilotTextHandlers.setFalse();
  };

  const handleRedoContent = () => {
    form.setValue("content", copilotText);
    isCopilotTextHandlers.setTrue();
  };

  const handleSelectPostType = (newPostType: MessageCreateRequest["type"]) => {
    form.setValue("postType", newPostType, {
      shouldDirty: true,
      shouldValidate: true,
    });

    // Trigger revalidation if the user changes the post type after selecting an audience
    if (form.formState.dirtyFields["projects"])
      form.setValue("projects", [], {
        shouldValidate: true,
      });
  };

  const handleSelectAudience = ({ group, projects }: { group: SystemGroupDto; projects: SimpleProjectDto[] }) => {
    form.setValue("group", group, {
      shouldDirty: true,
    });
    form.setValue("projects", projects, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleSelectScheduleDate = (date: Date | undefined) => {
    form.setValue("scheduleDate", date, {
      shouldDirty: true,
    });
  };

  const handleApplyTemplate = ({
    template,
    title,
    content,
  }: {
    template: PostTemplateDto;
    title: string;
    content: string;
  }) => {
    const postType =
      allowedPostTypes.length > 1
        ? allowedPostTypes.find((allowedPostType) => allowedPostType === template.postType)
        : allowedPostTypes[0];

    form.reset({
      title,
      content,
      postType,
      images: currImages,
      videos: currVideos,
      documents: currDocuments,
      scheduleDate: currScheduleDate,
      group: currGroup,
      projects: currProjects,
    });

    setTemplateInUse(template);
  };

  const handleClearTemplate = () => {
    form.reset({
      ...defaultFormValues,
      images: currImages,
      videos: currVideos,
      documents: currDocuments,
      scheduleDate: currScheduleDate,
      group: currGroup,
      projects: currProjects,
    });

    setTemplateInUse(undefined);
  };

  if (isPendingPortfolioPermissions || isPendingTemplates) {
    return <FullSizeLoader withPadding />;
  }

  // Error on templates should not block page usage
  if (errorPortfolioPermissions) {
    return <ErrorPage error={errorPortfolioPermissions} />;
  }

  if (!portfolioPermissions.canMassMessageAnnouncementPost && !portfolioPermissions.canMassMessageGeneralPost) {
    return <ErrorPage status={403} />;
  }

  const isUndoable = isCopilotText;
  const isRedoable = Boolean(copilotText) && !isCopilotText;
  const isCopilotAvailable = currContent.trim().split(" ").length >= 3;
  const currGroupName = currGroup?.translations?.find(
    (translation) => translation.languageId === sessionUser.language.id,
  )?.name;

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        tabTitle={t("page.create-mass-message.title")}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: t("page.portfolio.title"),
              },
              {
                name: t("page.create-mass-message.title"),
              },
            ]}
          />
        }
      >
        <PageGrid.Row>
          <PageGrid.Column size={useRedesignedForm ? "66%" : "75%"} isDesktopIndifferent>
            <Form.Root formMethods={form} onSubmit={() => confirmModalHandler.setTrue()}>
              {useRedesignedForm ? (
                <CreateMassPostRedesignForm
                  templates={templates ?? []}
                  isErrorTemplates={isErrorTemplates}
                  templateInUse={templateInUse}
                  onApplyTemplate={handleApplyTemplate}
                  onClearTemplate={handleClearTemplate}
                  isUndoable={isUndoable}
                  isRedoable={isRedoable}
                  isCopilotAvailable={isCopilotAvailable}
                  isGenerating={copilotResponseMutation.isPending}
                  onGenerate={handleGenerateContent}
                  onUndo={handleUndoContent}
                  onRedo={handleRedoContent}
                  audienceSelector={
                    <CreateMassPostFormAudienceSelector
                      selectedPostType={currPostType ?? "announcementReadOnly"}
                      selectedGroup={currGroup}
                      selectedProjects={currProjects}
                      isError={Boolean(form.formState.errors.projects)}
                      onConfirmSelection={handleSelectAudience}
                    />
                  }
                  selectedScheduleDate={currScheduleDate}
                  onSelectScheduleDate={handleSelectScheduleDate}
                  onCancel={cancelModalHandler.setTrue}
                  isSubmitting={isSubmitting}
                  canConfigureComments={
                    allowedPostTypes.includes("announcement") && allowedPostTypes.includes("announcementReadOnly")
                  }
                />
              ) : (
                <section className="flex w-full flex-col gap-space-lg">
                  {/* Templates */}
                  {!isErrorTemplates && (
                    <AdminCreatePostFormTemplateSelector templates={templates} onApplyTemplate={handleApplyTemplate} />
                  )}
                  {/* Post type */}
                  <SectionCard title={t("page.admin-create-post.section.step-1.title")} icon={<FileQuestion01Icon />}>
                    <AdminCreatePostFormTypeSelector
                      allowedTypes={allowedPostTypes}
                      selectedType={currPostType}
                      onSelectType={handleSelectPostType}
                    />
                  </SectionCard>
                  {/* Post content */}
                  <SectionCard
                    title={t("page.admin-create-post.section.step-2.title")}
                    renderAction={
                      templateInUse
                        ? () => (
                            <Button
                              size="sm"
                              icon={<XCloseIcon />}
                              variant="ghostPrimary"
                              onClick={handleClearTemplate}
                            >
                              {t("page.admin-create-post.section.step-2.btn.clear-template", {
                                templateName: templateInUse.name,
                              })}
                            </Button>
                          )
                        : undefined
                    }
                    icon={<PencilLineIcon />}
                  >
                    <AdminCreatePostFormAIHelper
                      isUndoable={isUndoable}
                      isRedoable={isRedoable}
                      isCopilotAvailable={isCopilotAvailable}
                      isGenerating={copilotResponseMutation.isPending}
                      onGenerate={handleGenerateContent}
                      onUndo={handleUndoContent}
                      onRedo={handleRedoContent}
                    />
                    <AdminCreatePostFormEditor />
                    {/* App preview (Mobile) */}
                    <aside className="mt-space-md flex flex-wrap items-center gap-space-sm @md:hidden">
                      <Button icon={<MessageSmileSquareIcon />} variant="subtle" onClick={tipsModalHandler.setTrue}>
                        {t("page.admin-create-post.form.btn.show-tips")}
                      </Button>
                      <CreatePostAppPreview
                        title={currTitle}
                        content={currContent}
                        images={currImages}
                        documents={currDocuments}
                        videos={currVideos}
                        groupName={currGroupName}
                        postType={
                          portfolioPermissions.canMassMessageAnnouncementPost
                            ? currPostType || "announcement"
                            : "undefined"
                        }
                        poll={currPoll}
                        fallbackBackgroundImage={sessionUser.project.backgroundImage}
                      />
                    </aside>
                  </SectionCard>
                  {/* Post audience */}
                  <SectionCard icon={<Settings04Icon />} title={t("page.admin-create-post.section.step-3.title")}>
                    <CreateMassPostFormAudienceSelector
                      selectedPostType={currPostType}
                      selectedGroup={currGroup}
                      selectedProjects={currProjects}
                      isError={!!form.formState.errors.projects}
                      onConfirmSelection={handleSelectAudience}
                    />
                    <Inline gap="sm" align="center">
                      <FormCheckbox
                        label={t("page.admin-create-post.form.email-preview-only.label")}
                        name="isEmailPreviewOnly"
                      />
                      <Info tooltip={t("page.admin-create-post.form.email-preview-only.info")} />
                    </Inline>
                    <ActionBar
                      secondary={
                        <Button fullWidth variant="subtle" tone="error" onClick={cancelModalHandler.setTrue}>
                          {t("common.action.cancel")}
                        </Button>
                      }
                    >
                      <AdminCreatePostFormScheduleSelector
                        selectedDate={currScheduleDate}
                        onSelectDate={handleSelectScheduleDate}
                      />
                      <Button type="submit" disabled={isSubmitting}>
                        {currScheduleDate
                          ? t("page.admin-create-mass-message.form.btn.cta-schedule")
                          : t("page.admin-create-mass-message.form.btn.cta-post")}
                      </Button>
                    </ActionBar>
                  </SectionCard>
                </section>
              )}
            </Form.Root>
          </PageGrid.Column>

          {/* App preview (Desktop) */}
          <PageGrid.Column size={useRedesignedForm ? "33%" : "25%"} isDesktopIndifferent>
            <aside className="sticky top-space-md hidden w-full self-start @md:block">
              <SectionCard variant="outlined">
                <CreatePostAppPreview
                  title={currTitle}
                  content={currContent}
                  images={currImages}
                  documents={currDocuments}
                  videos={currVideos}
                  groupName={currGroupName}
                  postType={
                    portfolioPermissions.canMassMessageAnnouncementPost ? currPostType || "announcement" : "undefined"
                  }
                  poll={currPoll}
                  fallbackBackgroundImage={sessionUser.project.backgroundImage}
                />
                <footer className="mx-auto w-fit">
                  <Button icon={<MessageSmileSquareIcon />} variant="subtle" onClick={tipsModalHandler.setTrue}>
                    {t("page.admin-create-post.form.btn.show-tips")}
                  </Button>
                </footer>
              </SectionCard>
            </aside>
          </PageGrid.Column>
        </PageGrid.Row>
      </DocumentPage>
      <ConfirmModal
        tone="primary"
        title={t("page.admin-create-post.cancel-modal.title")}
        description={t("page.admin-create-post.cancel-modal.description")}
        isOpened={isCancelModalOpened}
        shouldCloseOnEsc
        onReject={cancelModalHandler.setFalse}
        onOpenChange={cancelModalHandler.set}
        rejectBtnProps={{
          text: t("common.action.cancel"),
        }}
        onResolve={handleCancel}
        resolveBtnProps={{
          text: t("common.action.confirm"),
        }}
        isLoading={false}
      />
      <AdminCreatePostTipsModal isOpened={isTipsModalOpened} onOpenChange={tipsModalHandler.set} />
      <ConfirmModal
        tone="primary"
        title={t("page.create-mass-message.confirm-modal.title")}
        description={t("page.create-mass-message.confirm-modal.description-count", {
          count: form.getValues("projects")?.length || 0,
        })}
        isLoading={false}
        onReject={confirmModalHandler.setFalse}
        onOpenChange={confirmModalHandler.set}
        onResolve={handleCreatePost}
        resolveBtnProps={{
          text: t("common.action.confirm"),
          "data-testid": "confirm-btn",
        }}
        isOpened={isConfirmModalOpened}
        data-testid="mass-message-confirm-modal"
        shouldCloseOnEsc
      />

      {currScheduleDate && (
        <CommunityPostReachDetailsModal
          isOpened={isSuccessModalOpened}
          onOpenChange={(state) => {
            if (!state) {
              reset();
            }
            successModalHandler.set(state);
          }}
          reach={reach}
          status={CommunityPostStatus.JUST_SCHEDULED}
          scheduledDate={currScheduleDate.toISOString()}
          postType={currPoll ? "poll" : "message"}
        />
      )}
      {!currScheduleDate && (
        <CommunityPostReachDetailsModal
          isOpened={isSuccessModalOpened}
          onOpenChange={(state) => {
            if (!state) {
              reset();
            }
            successModalHandler.set(state);
          }}
          reach={reach}
          status={CommunityPostStatus.JUST_DELIVERED}
          postType={currPoll ? "poll" : "message"}
        />
      )}
    </>
  );
}
